import { useMemo } from 'react';
import type { ExtractAtomValue } from 'jotai';
import { atom, useAtomValue } from 'jotai';
import type { AnnotationLabelT } from '../..';
import { DEFAULT_BRUSH_TOOL_SETTINGS, GRID_MODE } from '../../labelling/constants';
import type { BrushSettingsT, GridModeT, SubToolT, ToolT } from '../../labelling/types';
import { DEFAULT_POLYGON_EPSILON } from '../../labelling/utils/mask-to-polygon';
import { TimeDisplayMode } from '../../types';
import type { SuperPixelT } from '../types/superPixel';
import atomWithDebounce from '../utils/atom-with-debounce';
import { expandAnnotationIdsAtom } from './timeline-expansion-atoms';
export const selectedAnnotationLabelAtom = atom<AnnotationLabelT>();
export const selectedToolAtom = atom<ToolT>('POINTER');
export const copiedAnnotationIdAtom = atom<string | null>(null);
export const selectedSubToolAtom = atom<SubToolT>();
export const brushToolSettingsAtom = atom<BrushSettingsT>(DEFAULT_BRUSH_TOOL_SETTINGS);
const baseSelectedAnnotationIdsAtom = atom<Array<string>>([]);
export const selectedAnnotationIdsAtom = atom(get => get(baseSelectedAnnotationIdsAtom), (get, set, ids: Array<string>) => {
  set(baseSelectedAnnotationIdsAtom, ids);
  if (ids?.length > 0) {
    set(expandAnnotationIdsAtom, ids);
  }
});
export const isAnnotationSelectedAtomCreator = (annotationId: string | undefined) => atom(get => annotationId ? get(selectedAnnotationIdsAtom).includes(annotationId) : false);
export const useIsAnnotationSelected = (annotationId: string | undefined) => {
  const isAnnotationSelectedAtom = useMemo(() => isAnnotationSelectedAtomCreator(annotationId), [annotationId]);
  return useAtomValue(isAnnotationSelectedAtom);
};
export const firstSelectedAnnotationIdAtom = atom<string | undefined>(get => get(selectedAnnotationIdsAtom)[0]);
export const multiSelectEnabledAtom = atom(false);
export const focusedAnnotationIdAtom = atom<string>();
export const gridModeAtom = atom<GridModeT>(GRID_MODE.NONE);
export const snapEnabledAtom = atom(false);
export const showAnnotationDetailsAtom = atom(false);
export const isLoadingAtom = atom(false);
export const isSAM3EnabledAtom = atom(false);
export const isSAM3PolygonToolsEnabledAtom = atom(false);
export const isSAM3PolygonTightnessSliderEnabledAtom = atom(false);
export const {
  currentValueAtom: polygonEpsilonAtom,
  debouncedValueAtom: debouncedPolygonEpsilonAtom
} = atomWithDebounce(DEFAULT_POLYGON_EPSILON, 120);
export type PendingPolygonReviewT = {
  annotationId: string;
  rawContour: [number, number][];
};
export const pendingPolygonReviewAtom = atom<PendingPolygonReviewT | null>(null);
export const timeDisplayModeAtom = atom<TimeDisplayMode>(TimeDisplayMode.FRAMES);
export const superpixelAtom = atom<SuperPixelT>({});

// Set when a frame-classification label is clicked but no annotation has been
// saved for it yet — lets the drawer open a create-mode panel without writing
// a placeholder annotation into StudioStore first.
export const draftFrameClassificationAtom = atom<{
  annotationId: string;
  annotationLabel: AnnotationLabelT;
} | null>(null);
export type ToolAtomHydrationList = [typeof selectedAnnotationLabelAtom, ExtractAtomValue<typeof selectedAnnotationLabelAtom>] | [typeof selectedToolAtom, ExtractAtomValue<typeof selectedToolAtom>] | [typeof copiedAnnotationIdAtom, ExtractAtomValue<typeof copiedAnnotationIdAtom>] | [typeof selectedSubToolAtom, ExtractAtomValue<typeof selectedSubToolAtom>] | [typeof brushToolSettingsAtom, ExtractAtomValue<typeof brushToolSettingsAtom>] | [typeof selectedAnnotationIdsAtom, ExtractAtomValue<typeof selectedAnnotationIdsAtom>] | [typeof multiSelectEnabledAtom, ExtractAtomValue<typeof multiSelectEnabledAtom>] | [typeof focusedAnnotationIdAtom, ExtractAtomValue<typeof focusedAnnotationIdAtom>] | [typeof gridModeAtom, ExtractAtomValue<typeof gridModeAtom>] | [typeof snapEnabledAtom, ExtractAtomValue<typeof snapEnabledAtom>] | [typeof showAnnotationDetailsAtom, ExtractAtomValue<typeof showAnnotationDetailsAtom>] | [typeof isLoadingAtom, ExtractAtomValue<typeof isLoadingAtom>] | [typeof isSAM3EnabledAtom, ExtractAtomValue<typeof isSAM3EnabledAtom>] | [typeof isSAM3PolygonToolsEnabledAtom, ExtractAtomValue<typeof isSAM3PolygonToolsEnabledAtom>] | [typeof isSAM3PolygonTightnessSliderEnabledAtom, ExtractAtomValue<typeof isSAM3PolygonTightnessSliderEnabledAtom>] | [typeof timeDisplayModeAtom, ExtractAtomValue<typeof timeDisplayModeAtom>] | [typeof superpixelAtom, ExtractAtomValue<typeof superpixelAtom>];